import styled, { css } from 'styled-components';
import { LuX } from 'react-icons/lu';
import { withArticle } from './corrections';

/**
 * The correction toolbar on the player (design: corrections_design.html, section 2), shown after "Correct
 * detections": Add · click (A), Draw a box (B), Remove (R), then the assistant's two modes, whose question and price
 * are in the count panel, and ✕ (Esc) back to watching. What the chosen tool does is said in a pill at the bottom
 * of the picture; `flash` replaces it for a moment after an action.
 */

const tk = ({ theme: t }) => t.token;

const Tools = styled.div`
	position: absolute;
	left: ${p => tk(p).marginSM}px;
	top: ${p => tk(p).marginSM}px;
	z-index: 5;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	max-width: calc(100% - ${p => tk(p).marginSM * 2}px);
	gap: ${p => tk(p).lineWidthBold}px;
	padding: ${p => tk(p).paddingXXS}px;
	border-radius: ${p => tk(p).borderRadiusLG}px;
	background: ${p => tk(p).panelGlassBg};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
`;

const Tool = styled.button`
	display: inline-flex;
	align-items: center;
	gap: ${p => tk(p).marginXS}px;
	padding: ${p => `${tk(p).paddingXXS}px ${tk(p).paddingSM}px`};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} transparent`};
	border-radius: ${p => tk(p).borderRadius}px;
	background: transparent;
	color: ${p => tk(p).colorTextSecondary};
	font-size: ${p => tk(p).fontSizeXS}px;
	font-weight: ${p => tk(p).fontWeightStrong};
	white-space: nowrap;
	cursor: pointer;
	&:hover {
		border-color: ${p => tk(p).colorBorder};
	}
	kbd {
		background: transparent;
		font-size: ${p => tk(p).fontSizeXXS}px;
		line-height: 1;
		color: ${p => tk(p).colorTextTertiary};
		border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
		border-radius: ${p => tk(p).borderRadiusXS}px;
		padding: ${p => `${tk(p).lineWidthBold}px ${tk(p).paddingXXS}px`};
	}
	${p =>
		p.$on &&
		css`
			background: ${tk(p).colorBrandDark};
			border-color: ${tk(p).colorBrandDark};
			color: ${tk(p).colorTextLightSolid};
			kbd {
				color: ${tk(p).colorTextLightSolid};
				border-color: ${tk(p).colorTextLightSolid};
			}
		`}
`;

const Sep = styled.span`
	align-self: stretch;
	width: ${p => tk(p).lineWidth}px;
	margin: ${p => `${tk(p).marginXXS}px ${tk(p).lineWidthBold}px`};
	background: ${p => tk(p).colorBorder};
`;

const Hint = styled.div`
	position: absolute;
	left: 50%;
	bottom: ${p => tk(p).marginSM}px;
	transform: translateX(-50%);
	z-index: 5;
	max-width: calc(100% - ${p => tk(p).marginSM * 2}px);
	padding: ${p => `${tk(p).paddingXXS}px ${tk(p).paddingSM}px`};
	border-radius: ${p => tk(p).borderRadiusLG * 4}px;
	background: ${p => tk(p).panelGlassBg};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
	color: ${p => tk(p).colorText};
	font-size: ${p => tk(p).fontSizeXS}px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	pointer-events: none;
`;

const TOOL_HINT = {
	click: s => `Click ${withArticle(s)} that has no dot`,
	box: s => `Drag a box around ${withArticle(s)} that has no dot`,
	remove: () => 'Click a dot to remove its object',
	again: () => 'The assistant checks every dot on this frame · ask in the panel',
	tell: () => 'Say what it missed in the panel',
};

const TOOLS = [
	{ key: 'click', label: 'Add · click', kbd: 'A' },
	{ key: 'box', label: 'Draw a box', kbd: 'B' },
	{ key: 'remove', label: 'Remove', kbd: 'R' },
];

const CorrectionToolbar = ({ subject, tool, onTool, onDone, flash = null }) => {
	const hint = flash || TOOL_HINT[tool]?.(subject);
	return (
		<>
			<Tools
				role="toolbar"
				aria-label="Correct detections"
				onPointerDown={e => e.stopPropagation()}>
				{TOOLS.map(t => (
					<Tool
						key={t.key}
						type="button"
						$on={tool === t.key}
						onClick={() => onTool(t.key)}>
						{t.label} <kbd>{t.kbd}</kbd>
					</Tool>
				))}
				<Sep />
				<Tool type="button" $on={tool === 'again'} onClick={() => onTool('again')}>
					Look again
				</Tool>
				<Tool type="button" $on={tool === 'tell'} onClick={() => onTool('tell')}>
					Tell it what it missed
				</Tool>
				<Sep />
				<Tool
					type="button"
					onClick={onDone}
					title="Back to watching (Esc)"
					aria-label="Done">
					<LuX />
				</Tool>
			</Tools>
			{hint && <Hint>{hint}</Hint>}
		</>
	);
};

export default CorrectionToolbar;
